'use client';

import { cn } from '@core/utils';
import type { KeyboardEvent, ReactNode } from 'react';
import {
  createContext,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import SvgChevrondown from '../../../__generated__/icon/SvgChevrondown';

type AccordionHeadingLevel = 2 | 3 | 4 | 5 | 6;
type AccordionVariant = 'inline' | 'separated';

interface AccordionContextValue {
  values: string[];
  multiple: boolean;
  disabled: boolean;
  variant: AccordionVariant;
  toggleValue: (value: string) => void;
}

const AccordionContext = createContext<AccordionContextValue | undefined>(
  undefined,
);

const useAccordionContext = () => {
  const context = useContext(AccordionContext);

  if (!context) {
    throw new Error('Accordion.Item은 Accordion 내부에서 사용해야 합니다.');
  }

  return context;
};

interface AccordionItemContextValue {
  value: string;
  disabled: boolean;
  isOpen: boolean;
  triggerId: string;
  contentId: string;
}

const AccordionItemContext = createContext<
  AccordionItemContextValue | undefined
>(undefined);

const useAccordionItemContext = () => {
  const context = useContext(AccordionItemContext);

  if (!context) {
    throw new Error(
      'Accordion.Trigger/Content는 Accordion.Item 내부에서 사용해야 합니다.',
    );
  }

  return context;
};

export interface AccordionProps {
  children: ReactNode;
  multiple?: boolean;
  values?: string[];
  defaultValues?: string[];
  onValuesChange?: (values: string[]) => void;
  disabled?: boolean;
  variant?: AccordionVariant;
  className?: string;
}

const AccordionRoot = ({
  children,
  multiple = false,
  values,
  defaultValues,
  onValuesChange,
  disabled = false,
  variant = 'inline',
  className,
}: AccordionProps) => {
  const [internalValues, setInternalValues] = useState<string[]>(
    defaultValues ?? [],
  );
  const isControlled = values !== undefined;
  const currentValues = isControlled ? values : internalValues;
  const containerRef = useRef<HTMLDivElement>(null);

  const contextValue = useMemo<AccordionContextValue>(() => {
    const toggleValue = (value: string) => {
      const isOpen = currentValues.includes(value);
      const nextValues = multiple
        ? isOpen
          ? currentValues.filter((currentValue) => currentValue !== value)
          : [...currentValues, value]
        : isOpen
          ? []
          : [value];

      if (!isControlled) {
        setInternalValues(nextValues);
      }
      onValuesChange?.(nextValues);
    };

    return { values: currentValues, multiple, disabled, variant, toggleValue };
  }, [
    currentValues,
    multiple,
    disabled,
    variant,
    isControlled,
    onValuesChange,
  ]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (!container) return;

    const triggers = Array.from(
      container.querySelectorAll<HTMLButtonElement>(
        '[data-accordion-trigger]:not(:disabled)',
      ),
    );
    if (triggers.length === 0) return;

    const currentIndex = triggers.indexOf(
      document.activeElement as HTMLButtonElement,
    );

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowDown':
        nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % triggers.length;
        break;
      case 'ArrowUp':
        nextIndex =
          currentIndex < 0
            ? triggers.length - 1
            : (currentIndex - 1 + triggers.length) % triggers.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = triggers.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    triggers[nextIndex]?.focus();
  };

  return (
    <AccordionContext.Provider value={contextValue}>
      <div
        ref={containerRef}
        role="presentation"
        className={cn(
          'flex flex-col',
          variant === 'separated' && 'gap-8',
          className,
        )}
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>
    </AccordionContext.Provider>
  );
};

export interface AccordionItemProps {
  value: string;
  disabled?: boolean;
  children: ReactNode;
  className?: string;
}

const AccordionItem = ({
  value,
  disabled = false,
  children,
  className,
}: AccordionItemProps) => {
  const { values, disabled: rootDisabled, variant } = useAccordionContext();
  const generatedId = useId();
  const isOpen = values.includes(value);
  const itemDisabled = rootDisabled || disabled;

  const itemContextValue = useMemo<AccordionItemContextValue>(
    () => ({
      value,
      disabled: itemDisabled,
      isOpen,
      triggerId: `${generatedId}-trigger`,
      contentId: `${generatedId}-content`,
    }),
    [value, itemDisabled, isOpen, generatedId],
  );

  return (
    <AccordionItemContext.Provider value={itemContextValue}>
      <div
        className={cn(
          variant === 'inline' && 'border-b border-Gray-200 last:border-b-0',
          variant === 'separated' &&
            'rounded-small border border-Gray-300 bg-Gray-white',
          className,
        )}
      >
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
};

export interface AccordionTriggerProps {
  title: ReactNode;
  description?: ReactNode;
  headingLevel?: AccordionHeadingLevel;
  ellipsis?: boolean;
  className?: string;
}

const AccordionTrigger = ({
  title,
  description,
  headingLevel = 3,
  ellipsis = false,
  className,
}: AccordionTriggerProps) => {
  const { toggleValue } = useAccordionContext();
  const { value, disabled, isOpen, triggerId, contentId } =
    useAccordionItemContext();
  const HeadingTag = `h${headingLevel}` as `h${AccordionHeadingLevel}`;

  return (
    <HeadingTag className="m-0">
      <button
        type="button"
        id={triggerId}
        data-accordion-trigger=""
        aria-expanded={isOpen}
        aria-controls={contentId}
        disabled={disabled}
        onClick={() => toggleValue(value)}
        className={cn(
          'flex w-full items-center justify-between gap-12 bg-transparent p-16 text-left outline-none transition-colors duration-150 ease-in-out',
          'hover:bg-Gray-50 active:bg-Gray-100',
          'focus-visible:bg-Gray-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-Gray-black/50',
          'disabled:cursor-not-allowed disabled:bg-transparent disabled:opacity-50',
          className,
        )}
      >
        <span
          className={cn(
            'flex flex-1 flex-col items-start gap-4 text-start',
            ellipsis && 'min-w-0',
          )}
        >
          <span
            className={cn(
              'font-600 text-Gray-900 typo-body3',
              ellipsis && 'w-full truncate',
            )}
          >
            {title}
          </span>
          {description ? (
            <span className="font-400 text-Gray-600 typo-body4">
              {description}
            </span>
          ) : null}
        </span>
        <SvgChevrondown
          aria-hidden="true"
          className={cn(
            'size-16 shrink-0 text-Gray-500 transition-transform duration-200 motion-reduce:transition-none',
            isOpen && 'rotate-180',
          )}
        />
      </button>
    </HeadingTag>
  );
};

export interface AccordionContentProps {
  children: ReactNode;
  className?: string;
}

const AccordionContent = ({ children, className }: AccordionContentProps) => {
  const { isOpen, triggerId, contentId } = useAccordionItemContext();

  return (
    <div
      id={contentId}
      role="region"
      aria-labelledby={triggerId}
      className={cn(
        'grid transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none',
        isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
      )}
    >
      <div className="overflow-hidden">
        <div
          className={cn('px-16 pb-16 pt-8 text-Gray-700 typo-body4', className)}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export const Accordion = Object.assign(AccordionRoot, {
  Item: AccordionItem,
  Trigger: AccordionTrigger,
  Content: AccordionContent,
});
